import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import {
  BrandFetchRes,
  EmailSenderRes,
  ShopifyConfigReq,
  Tenant,
  TenantCreateReq,
  TenantCreateRes,
  TenantPatch,
  TenantsListRes,
  VerifyDomainRes,
} from '../models';

@Injectable({ providedIn: 'root' })
export class TenantsApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/admin/tenants`;

  getTenants(): Observable<TenantsListRes> {
    return this.http.get<TenantsListRes>(this.baseUrl);
  }

  updateTenant(tenantId: string, patch: TenantPatch): Observable<Tenant> {
    return this.http
      .put<{ tenant: Tenant }>(`${this.baseUrl}/${encodeURIComponent(tenantId)}`, patch)
      .pipe(map((res) => res.tenant));
  }

  /** Registers a new tenant; the response's clientSecret is shown once and never retrievable. */
  createTenant(req: TenantCreateReq): Observable<TenantCreateRes> {
    return this.http.post<TenantCreateRes>(this.baseUrl, req);
  }

  /** Runs the server-side DNS / TLS / discovery checks for the tenant's login domain. */
  verifyDomain(tenantId: string): Observable<VerifyDomainRes> {
    return this.http.post<VerifyDomainRes>(
      `${this.baseUrl}/${encodeURIComponent(tenantId)}/verify-domain`,
      {},
    );
  }

  /** Saves the Shopify store linkage (redirect + sign-out URLs). */
  saveShopifyConfig(tenantId: string, req: ShopifyConfigReq): Observable<Tenant> {
    return this.http
      .put<{ tenant: Tenant }>(`${this.baseUrl}/${encodeURIComponent(tenantId)}/shopify`, req)
      .pipe(map((res) => res.tenant));
  }

  /** Marks the registration live after the operator's test sign-in. */
  confirmTenant(tenantId: string): Observable<Tenant> {
    return this.http
      .post<{ tenant: Tenant }>(`${this.baseUrl}/${encodeURIComponent(tenantId)}/confirm`, {})
      .pipe(map((res) => res.tenant));
  }

  /** Sets the SendGrid fallback sender; provisions the domain authentication and returns its DNS records. */
  saveEmailSender(tenantId: string, from: string): Observable<EmailSenderRes> {
    return this.http.put<EmailSenderRes>(
      `${this.baseUrl}/${encodeURIComponent(tenantId)}/email-sender`,
      { from },
    );
  }

  /** Asks SendGrid to re-check the sender domain's DNS records. */
  verifyEmailSender(tenantId: string): Observable<EmailSenderRes> {
    return this.http.post<EmailSenderRes>(
      `${this.baseUrl}/${encodeURIComponent(tenantId)}/email-sender/verify`,
      {},
    );
  }

  /** Rotates the OIDC client secret; the new value is shown once. */
  regenerateClientSecret(tenantId: string): Observable<{ clientId: string; clientSecret: string }> {
    return this.http.post<{ clientId: string; clientSecret: string }>(
      `${this.baseUrl}/${encodeURIComponent(tenantId)}/client-secret/regenerate`,
      {},
    );
  }

  /** Stores the retailer's own social OAuth credentials (secret set-only, to Secret Manager). */
  saveSocialCredentials(
    tenantId: string,
    provider: 'facebook' | 'google',
    id: string,
    secret: string,
  ): Observable<Tenant> {
    return this.http
      .put<{ tenant: Tenant }>(
        `${this.baseUrl}/${encodeURIComponent(tenantId)}/social/${provider}`,
        { id, secret },
      )
      .pipe(map((res) => res.tenant));
  }

  /** Stores the tenant's own Marketing Cloud package credentials (secret set-only, to Secret Manager). */
  saveSalesforceCredentials(tenantId: string, id: string, secret: string): Observable<Tenant> {
    return this.http
      .put<{ tenant: Tenant }>(
        `${this.baseUrl}/${encodeURIComponent(tenantId)}/salesforce/credentials`,
        { id, secret },
      )
      .pipe(map((res) => res.tenant));
  }

  /** Removes them; Salesforce sends fall back to the platform credentials. */
  removeSalesforceCredentials(tenantId: string): Observable<Tenant> {
    return this.http
      .delete<{ tenant: Tenant }>(
        `${this.baseUrl}/${encodeURIComponent(tenantId)}/salesforce/credentials`,
      )
      .pipe(map((res) => res.tenant));
  }

  /** Stores the tenant's own One Profile client credentials (secret set-only, to Secret Manager). */
  saveOneProfileCredentials(tenantId: string, id: string, secret: string): Observable<Tenant> {
    return this.http
      .put<{ tenant: Tenant }>(
        `${this.baseUrl}/${encodeURIComponent(tenantId)}/one-profile/credentials`,
        { id, secret },
      )
      .pipe(map((res) => res.tenant));
  }

  /** Removes them; One Profile stays inactive for the tenant until a new pair is set. */
  removeOneProfileCredentials(tenantId: string): Observable<Tenant> {
    return this.http
      .delete<{ tenant: Tenant }>(
        `${this.baseUrl}/${encodeURIComponent(tenantId)}/one-profile/credentials`,
      )
      .pipe(map((res) => res.tenant));
  }

  /** Removes the social credentials; the method disappears from the login screen. */
  removeSocialCredentials(tenantId: string, provider: 'facebook' | 'google'): Observable<Tenant> {
    return this.http
      .delete<{ tenant: Tenant }>(
        `${this.baseUrl}/${encodeURIComponent(tenantId)}/social/${provider}`,
      )
      .pipe(map((res) => res.tenant));
  }

  /** Fetches brand candidates (colours / favicon / logos) off a storefront URL. */
  fetchBrand(url: string): Observable<BrandFetchRes> {
    return this.http.post<BrandFetchRes>(`${environment.apiBaseUrl}/admin/brand-fetch`, { url });
  }

  /** Uploads a login-page logo/favicon; returns the /brand-assets/... path for the theme. */
  uploadThemeAsset(
    tenantId: string,
    kind: 'logo' | 'favicon',
    contentType: string,
    dataBase64: string,
  ): Observable<{ path: string }> {
    return this.http.post<{ path: string }>(
      `${this.baseUrl}/${encodeURIComponent(tenantId)}/theme-asset`,
      { kind, contentType, dataBase64 },
    );
  }
}
